import type { LayerConfig, LegendPaint } from '../../api/types';
import { metresPerPixel } from '../scaleBar';

/**
 * Layers Pro's Symbology and Labels editors (production's: a single symbol's colour, outline and size; a layer's labels
 * from any field, with colour, size, halo and a scale range). An edit is the layer's style with the change written in,
 * so the map, the legend and a print all show it; Reset puts the layer's own style back.
 */

export type SymbolKind = 'point' | 'line' | 'polygon';

export interface Symbology {
  /** Fill (a point or an area) or the line itself. */
  fill: string;
  /** A point's ring or an area's edge. */
  outline: string;
  /** A point's diameter, a line's width or an area's edge width, in pixels. */
  size: number;
}

export interface LabelStyle {
  field: string;
  colour: string;
  size: number;
  halo: number;
  /** Labels show once zoomed in past 1:minScale; 0 is any zoom. */
  minScale: number;
  /** And hide again past 1:maxScale; 0 is never. */
  maxScale: number;
}

/** Production's scale presets for Show from and until. */
export const MIN_SCALES = [500000, 250000, 100000, 50000, 25000, 10000, 5000, 2500] as const;
export const MAX_SCALES = [25000, 10000, 5000, 2500, 1000, 500] as const;

/** The project label font (the server's LABEL_FONT), so an edited label draws from the same glyphs. */
const LABEL_FONT = 'Public Sans Regular';
const PX_PER_METRE = 96 / 0.0254;

export function kindOf(layer: Pick<LayerConfig, 'geometry'>): SymbolKind {
  const t = layer.geometry.type;
  return /Point/.test(t) ? 'point' : /LineString/.test(t) ? 'line' : 'polygon';
}

type Style = Readonly<Record<string, unknown>>;

const paintOf = (l: Style): Record<string, unknown> => ({ ...((l['paint'] as Record<string, unknown>) ?? {}) });
const isLabel = (l: Style): boolean => typeof l['id'] === 'string' && l['id'].endsWith(':label');

function hex(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  if (/^#[0-9a-f]{3}$/.test(s)) return `#${[...s.slice(1)].map((c) => c + c).join('')}`;
  return null;
}

const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** What the Symbology editor opens on: the layer's own colours and size where it states plain ones, else defaults. */
export function symbologyOf(style: readonly Style[], kind: SymbolKind): Symbology {
  const of = (type: string): Record<string, unknown> => {
    const l = style.find((s) => s['type'] === type && !isLabel(s));
    return l ? paintOf(l) : {};
  };
  if (kind === 'point') {
    const p = of('circle');
    return {
      fill: hex(p['circle-color']) ?? '#2563eb',
      outline: hex(p['circle-stroke-color']) ?? '#ffffff',
      size: num(p['circle-radius'], 4) * 2,
    };
  }
  if (kind === 'line') {
    const p = of('line');
    return { fill: hex(p['line-color']) ?? '#2563eb', outline: '#ffffff', size: num(p['line-width'], 2) };
  }
  const f = of('fill');
  const l = of('line');
  return {
    fill: hex(f['fill-color']) ?? '#2563eb',
    outline: hex(l['line-color']) ?? hex(f['fill-outline-color']) ?? '#1d2939',
    size: num(l['line-width'], 1),
  };
}

/** The layer's style with a single symbol written in (every class drawn alike, as production's editor does). */
export function withSymbology(style: readonly Style[], kind: SymbolKind, s: Symbology): Record<string, unknown>[] {
  return style.map((l) => {
    if (isLabel(l)) return { ...l };
    const paint = paintOf(l);
    const t = l['type'];
    if (t === 'circle') {
      Object.assign(paint, { 'circle-color': s.fill, 'circle-radius': s.size / 2, 'circle-stroke-color': s.outline });
    } else if (t === 'line') {
      Object.assign(paint, kind === 'polygon' ? { 'line-color': s.outline } : { 'line-color': s.fill });
      paint['line-width'] = s.size;
    } else if (t === 'fill') {
      Object.assign(paint, { 'fill-color': s.fill, 'fill-outline-color': s.outline });
    } else {
      return { ...l };
    }
    return { ...l, paint };
  });
}

/** The legend's one entry for a single symbol. */
export function symbologyLegend(
  title: string,
  kind: SymbolKind,
  s: Symbology,
): { label: string; paint: LegendPaint }[] {
  const paint: LegendPaint =
    kind === 'point'
      ? { fill: s.fill, stroke: s.outline, strokeWidth: 1, radius: s.size / 2 }
      : kind === 'line'
        ? { stroke: s.fill, strokeWidth: s.size }
        : { fill: s.fill, fillOpacity: 0.35, stroke: s.outline, strokeWidth: s.size };
  return [{ label: title, paint }];
}

/** The map zoom at which the view is at 1:scale, at a latitude (the map's 512 px tiles, a 96 dpi screen). */
export function zoomForScale(scale: number, latitude: number): number {
  return Math.log2(metresPerPixel(0, latitude) / (scale / PX_PER_METRE));
}

/** The scale 1:N the map is at, at a zoom and latitude. */
export function scaleForZoom(zoom: number, latitude: number): number {
  return metresPerPixel(zoom, latitude) * PX_PER_METRE;
}

/** What the Labels editor opens on: the layer's own label where it has one, else its first field. */
export function labelStyleOf(style: readonly Style[], fields: readonly string[], latitude: number): LabelStyle {
  const l = style.find(isLabel);
  const layout = (l?.['layout'] as Record<string, unknown> | undefined) ?? {};
  const paint = l ? paintOf(l) : {};
  const tf = layout['text-field'];
  const field =
    Array.isArray(tf) && Array.isArray(tf[1]) && tf[1][0] === 'get' && typeof tf[1][1] === 'string'
      ? tf[1][1]
      : (fields[0] ?? '');
  const minZoom = typeof l?.['minzoom'] === 'number' ? l['minzoom'] : 0;
  return {
    field,
    colour: hex(paint['text-color']) ?? '#1a1a1a',
    size: num(layout['text-size'], 13),
    halo: num(paint['text-halo-width'], 1.2),
    minScale: minZoom > 0 ? Math.round(scaleForZoom(minZoom, latitude)) : 0,
    maxScale: 0,
  };
}

/** The layer's style with its label as the editor set it (added when the layer had none). */
export function withLabel(
  style: readonly Style[],
  layerId: string,
  kind: SymbolKind,
  s: LabelStyle,
  latitude: number,
): Record<string, unknown>[] {
  const label: Record<string, unknown> = {
    id: `${layerId}:label`,
    type: 'symbol',
    metadata: { 'mm:layer': layerId, 'mm:requires': 'glyphs' },
    layout: {
      'text-field': ['to-string', ['get', s.field]],
      'text-font': [LABEL_FONT],
      'text-size': s.size,
      'text-optional': true,
      'symbol-placement': kind === 'line' ? 'line' : 'point',
    },
    paint: { 'text-color': s.colour, 'text-halo-color': '#ffffff', 'text-halo-width': s.halo },
    ...(s.minScale > 0 ? { minzoom: Math.max(0, zoomForScale(s.minScale, latitude)) } : {}),
    ...(s.maxScale > 0 ? { maxzoom: Math.min(24, zoomForScale(s.maxScale, latitude)) } : {}),
  };
  return [...style.filter((l) => !isLabel(l)).map((l) => ({ ...l })), label];
}
